import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CancelModal from '@/app/common/components/CancelModal';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import { forgetPromptRunBatch, trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import {
  buildTrackerListBody,
  containsFilter,
  equalsFilter,
  type ColumnFilter,
} from '@/app/TrackerAi/api/queryParams';
import {
  useFetchProjectPromptListPageMutation,
  useGetProjectPromptListQuery,
} from '@/app/TrackerAi/api/trackerEndpointsApi';
import { isDuplicateExecutionError } from '@/app/TrackerAi/utils/api-errors';
import {
  enqueueRunErrorMessage,
  schedulePromptRunRefresh,
} from '@/app/TrackerAi/utils/prompt-run';
import {
  DuplicateExecutionNotice,
  ExecutionAlert,
} from '../executions/DuplicateExecutionNotice';
import {
  FilterChip,
  FilterMenuItem,
  ModelIcon as GenericModelIcon,
} from '../filters/FilterChip';
import { PromptInspector } from '../prompts/PromptInspector';
import { PromptBulkActions } from '../prompts/PromptBulkActions';
import { PromptsTable } from '../prompts/PromptsTable';
import { useProject } from '../../context/ProjectContext';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { GRID_PAGE_SIZE } from '../../utils/grid-fetch';
import { useTrackerAiPath } from '../../utils/paths';
import { getPromptBrandRows } from '../../utils/prompt-domain-metrics';
import { buildDateRangeQueryOptions } from '../../utils/visibility-range';
import type { DuplicateExecutionData, UserBrandInsight } from '../../types';

type TabFilter = 'all' | 'pending' | 'executed' | 'failed' | 'drafts';
type PendingDelete = { type: 'bulk' };

const STATUS_OPTIONS: { id: TabFilter; label: string }[] = [
  { id: 'all', label: 'همه وضعیت‌ها' },
  { id: 'pending', label: 'در انتظار' },
  { id: 'executed', label: 'اجرا شده' },
  { id: 'failed', label: 'ناموفق' },
  { id: 'drafts', label: 'پیش‌نویس' },
];

const STATUS_VALUES: Record<Exclude<TabFilter, 'all'>, string[]> = {
  pending: ['pending'],
  executed: ['executed', 'partial'],
  failed: ['failed'],
  drafts: ['draft'],
};

function promptTableFilters(tab: TabFilter, search: string): Record<string, ColumnFilter> {
  const filters: Record<string, ColumnFilter> = {};
  if (tab !== 'all') filters.status = equalsFilter(STATUS_VALUES[tab]);
  if (search.trim()) filters.search = containsFilter(search.trim());
  return filters;
}

export function TopicPromptsPanel({
  topicId,
  topicName,
}: {
  topicId: string;
  topicName: string;
}) {
  const {
    projectId,
    filtersReady,
    visibilityRange,
    customFrom,
    customTo,
    domainFilterId,
    models,
    error,
    setError,
    refresh,
    defaultModelId,
    modelFilterId,
    setNewPromptOpen,
    runningPromptIds,
  } = useProject();
  const navigate = useNavigate();
  const toTrackerAi = useTrackerAiPath();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [runningId, setRunningId] = useState<string | null>(null);
  const [runningBulk, setRunningBulk] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [duplicateNotice, setDuplicateNotice] = useState<DuplicateExecutionData | null>(null);
  const [tab, setTab] = useState<TabFilter>('all');
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');

  const debouncedSearch = useDebouncedValue(search, 300);
  const providerId = providerFilter || modelFilterId;

  const listArgs = useMemo(
    () => ({
      projectId: projectId ?? '',
      ...buildTrackerListBody(
        {
          ...buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
            topicId,
            providerIds: providerId ? [providerId] : undefined,
          }),
          domainFilter: domainFilterId,
        },
        promptTableFilters(tab, debouncedSearch),
      ),
    }),
    [
      projectId,
      visibilityRange,
      customFrom,
      customTo,
      topicId,
      providerId,
      domainFilterId,
      tab,
      debouncedSearch,
    ],
  );
  const { data, currentData, refetch, isError: isListError, error: listError, isFetching } =
    useGetProjectPromptListQuery(
    { ...listArgs, page: 1, perPage: GRID_PAGE_SIZE },
    { skip: !projectId || !filtersReady },
  );
  const [fetchPromptPage, { data: lastPage }] = useFetchProjectPromptListPageMutation();
  const refetchPrompts = useCallback(() => void refetch(), [refetch]);

  const prompts = useMemo(() => currentData?.rows ?? [], [currentData]);
  // Selection/run/inspector may target rows from the last fetched grid page, not only page 1.
  const knownPrompts = useMemo(() => {
    const byId = new Map(prompts.map(p => [p.id, p]));
    for (const item of lastPage?.rows ?? []) byId.set(item.id, item);
    return [...byId.values()];
  }, [prompts, lastPage]);

  const selected = knownPrompts.find(p => p.id === selectedId) ?? null;

  function selectPrompt(promptId: string) {
    setSelectedId(promptId);
  }

  function closeInspector() {
    setSelectedId(null);
  }

  function viewDuplicateResult(executionId: string) {
    setDuplicateNotice(null);
    void navigate(toTrackerAi('executions', `id=${encodeURIComponent(executionId)}`));
  }

  async function runPrompt(promptId: string) {
    const prompt = knownPrompts.find(p => p.id === promptId);
    const modelIds = prompt?.modelIds?.length
      ? prompt.modelIds
      : prompt?.modelId
        ? [prompt.modelId]
        : defaultModelId
          ? [defaultModelId]
          : [];
    if (!modelIds.length) {
      setError('قبل از اجرا، یک مدل AI در تنظیمات انتخاب کنید');
      return;
    }
    setRunningId(promptId);
    setError('');
    setDuplicateNotice(null);
    try {
      const result = await api.runPrompt(promptId);
      const enqueueError = enqueueRunErrorMessage(result);
      if (enqueueError) {
        setError(enqueueError);
      }
      await refetch();
      schedulePromptRunRefresh(refetchPrompts);
    } catch (e) {
      if (isDuplicateExecutionError(e)) {
        setDuplicateNotice(e.data);
        setError('');
        await refetch();
        selectPrompt(promptId);
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunningId(null);
    }
  }

  function requestDeleteSelected() {
    if (selectedIds.size === 0) return;
    setPendingDelete({ type: 'bulk' });
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeletingBulk(true);
    setError('');
    try {
      await api.deletePrompt([...selectedIds]);
      if (selectedId && selectedIds.has(selectedId)) {
        closeInspector();
      }
      setSelectedIds(new Set());
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingBulk(false);
    }
  }

  async function runSelected() {
    const toRun = knownPrompts.filter(
      p =>
        selectedIds.has(p.id) &&
        (p.status === 'pending' || p.status === 'failed' || p.status === 'draft'),
    );
    if (!toRun.length) return;
    setRunningBulk(true);
    setError('');
    setDuplicateNotice(null);
    try {
      const result = await api.enqueuePromptRuns({
        promptIds: toRun.map((p) => p.id),
      });
      const enqueueError = enqueueRunErrorMessage(result);
      if (enqueueError) {
        setError(enqueueError);
      }
      await refetch();
      schedulePromptRunRefresh(refetchPrompts);
    } catch (e) {
      if (isDuplicateExecutionError(e)) {
        setDuplicateNotice(e.data);
        setError('');
        await refetch();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunningId(null);
      setRunningBulk(false);
    }
  }

  const handleAddCompetitor = useCallback(
    async (brand: UserBrandInsight) => {
      if (!projectId) return;
      await api.createCompetitorFromExtracted(projectId, {
        brandName: brand.canonicalName ?? brand.brandName,
        detectedHost: brand.detectedHost ?? undefined,
      });
      await Promise.all([refresh(), refetch()]);
    },
    [projectId, refresh, refetch],
  );

  const handleCreateCompetitor = useCallback(
    async (competitor: { brandName: string; websiteUrl: string; aliases: string[] }) => {
      if (!projectId) return;
      await api.createCompetitor(projectId, competitor);
      await Promise.all([refresh(), refetch()]);
    },
    [projectId, refresh, refetch],
  );

  const providerOptions = useMemo(
    () => models.map(model => ({ id: model.id, name: model.publicName })),
    [models],
  );

  const statusLabel = STATUS_OPTIONS.find(option => option.id === tab)?.label ?? 'همه وضعیت‌ها';
  const providerLabel =
    providerOptions.find(p => p.id === providerFilter)?.name ?? 'همه پرووایدرها';
  const hasLocalFilters = Boolean(search.trim() || tab !== 'all' || providerFilter);

  return (
    <div className="bg-body-tertiary border-top border-bottom border-200">
      <div className="px-3 pt-3 pb-2 d-flex flex-column gap-2">
        <div className="d-flex align-items-center justify-content-between gap-3">
          <p className="fs--1 fw-semibold text-800 mb-0">
            پرامپت‌های <span className="text-primary">{topicName}</span>
          </p>
          {(data?.summary.total ?? 0) > 0 && (
            <Button
              type="button"
              size="sm"
              variant="falcon-primary"
              onClick={() => setNewPromptOpen(true, topicId)}
            >
              + پرامپت جدید
            </Button>
          )}
        </div>

        {duplicateNotice && (
          <DuplicateExecutionNotice
            duplicate={duplicateNotice}
            onViewResult={() => viewDuplicateResult(duplicateNotice.existingRequestId)}
          />
        )}

        {error && <ExecutionAlert message={error} className="fs--1 mb-0" />}

        <div
          className="d-flex flex-wrap align-items-center gap-2"
          onClick={e => e.stopPropagation()}
        >
          <div
            className="position-relative flex-grow-1"
            style={{ minWidth: '16rem', maxWidth: '24rem' }}
          >
            <FontAwesomeIcon
              icon="search"
              className="position-absolute top-50 translate-middle-y text-400"
              style={{ insetInlineStart: '0.75rem', pointerEvents: 'none' }}
            />
            <Form.Control
              type="search"
              size="sm"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="جستجوی پرامپت یا برند…"
              className="rounded-pill border-300"
              style={{ paddingInlineStart: '2.25rem' }}
            />
          </div>

          <FilterChip
            icon={<FontAwesomeIcon icon="check-circle" className="fs--2" />}
            label={statusLabel}
            active={tab !== 'all'}
          >
            {STATUS_OPTIONS.map(option => (
              <FilterMenuItem
                key={option.id}
                selected={tab === option.id}
                onClick={() => setTab(option.id)}
              >
                {option.label}
              </FilterMenuItem>
            ))}
          </FilterChip>

          {providerOptions.length > 0 && !modelFilterId && (
            <FilterChip
              icon={<GenericModelIcon />}
              label={providerLabel}
              active={Boolean(providerFilter)}
            >
              <FilterMenuItem selected={!providerFilter} onClick={() => setProviderFilter('')}>
                همه پرووایدرها
              </FilterMenuItem>
              {providerOptions.map(p => (
                <FilterMenuItem
                  key={p.id}
                  selected={providerFilter === p.id}
                  onClick={() => setProviderFilter(p.id)}
                >
                  {p.name}
                </FilterMenuItem>
              ))}
            </FilterChip>
          )}

          {hasLocalFilters && (
            <Button
              type="button"
              size="sm"
              variant="link"
              className="text-decoration-none text-500 px-1"
              onClick={() => {
                setSearch('');
                setTab('all');
                setProviderFilter('');
              }}
            >
              پاک کردن
            </Button>
          )}
        </div>

      </div>

      <div className="px-2 pb-3 ag-grid-in-card" onClick={e => e.stopPropagation()}>
        {shouldShowDataLoadError(isListError, listError) ? (
          <div className="py-4">
            <DataLoadError
              compact
              error={listError}
              onRetry={() => void refetch()}
              isRetrying={isFetching}
              isNetworkError={isNetworkApiError(listError)}
            />
          </div>
        ) : data?.summary.total === 0 && !hasLocalFilters && !modelFilterId ? (
          <Card className="border-dashed">
            <Card.Body className="py-5 text-center d-flex flex-column align-items-center">
              <p className="fs-0 fw-semibold text-800 mb-2">هنوز پرامپتی در این تاپیک نیست</p>
              <p className="fs--1 text-500 mb-0" style={{ maxWidth: '28rem' }}>
                از AI سوالی بپرسید تا دیده‌شدن برند را در پاسخ ردیابی کنید. هر خط یک پرامپت
                جداگانه است.
              </p>
              <Button
                type="button"
                size="sm"
                variant="falcon-primary"
                className="mt-3"
                onClick={() => setNewPromptOpen(true, topicId)}
              >
                + پرامپت جدید
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <>
            <PromptBulkActions
              count={selectedIds.size}
              running={runningBulk}
              deleting={deletingBulk}
              onRun={() => void runSelected()}
              onDelete={requestDeleteSelected}
            />
            <PromptsTable
              prompts={prompts}
              total={currentData?.total ?? 0}
              fetchPage={fetchPromptPage}
              additionalData={listArgs}
              loading={!currentData}
              selectedId={selectedId}
              runningId={runningId}
              runningIds={runningPromptIds}
              showTopic={false}
              height="420px"
              onSelect={selectPrompt}
              onSelectionChange={setSelectedIds}
              onRun={id => void runPrompt(id)}
              onStop={forgetPromptRunBatch}
            />
          </>
        )}
      </div>

      {selected && (
        <PromptInspector
          prompt={{
            id: selected.id,
            text: selected.text,
            status:
              selected.status === 'executed' || selected.status === 'partial'
                ? 'completed'
                : selected.status === 'draft'
                  ? 'draft'
                  : selected.status,
            responseText: null,
            executedAt: selected.lastRunAt,
            modelId: selected.modelId,
            model: selected.providers[0]?.publicName ?? null,
            providers: selected.providers,
            runsCount: selected.runs,
            brands: [],
          }}
          topicName={topicName}
          running={runningId === selected.id || runningPromptIds.includes(selected.id)}
          duplicateExecution={duplicateNotice}
          onViewDuplicateResult={viewDuplicateResult}
          onRun={() => void runPrompt(selected.id)}
          onClose={closeInspector}
          projectId={projectId ?? undefined}
          onAddCompetitor={handleAddCompetitor}
          onCreateCompetitor={handleCreateCompetitor}
          brandRows={getPromptBrandRows(selected)}
        />
      )}

      <CancelModal
        show={pendingDelete != null}
        setShow={() => setPendingDelete(null)}
        title="حذف پرامپت"
        submitTitle="آره، حذف بشه"
        isSubmitting={deletingBulk}
        submitFunc={confirmDelete}
        desc={`آیا مطمئن هستید می‌خواهید ${selectedIds.size} پرامپت انتخاب‌شده حذف شود؟ این عمل قابل بازگشت نیست.`}
      />
    </div>
  );
}
